import { useEffect, useRef, useState } from 'react';
import { animate, stagger } from 'animejs';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { useAnimatedCounter } from '../../hooks/useAnimatedCounter';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { calculateConnectionPaths, createResizeHandler, type Connection } from '../../lib/utils/pathCalculator';
import { Clock, Shield, Building2, Heart } from 'lucide-react';

interface StatCardProps {
    icon: React.ComponentType<{ className?: string }>;
    value: number;
    suffix: string;
    label: string;
    isInView: boolean;
    reducedMotion: boolean;
    index: number;
}

function StatCard({ icon: Icon, value, suffix, label, isInView, reducedMotion, index }: StatCardProps) {
    const { displayValue } = useAnimatedCounter(value, {
        isInView,
        duration: 2000,
        suffix
    });

    return (
        <div
            className={`stat-card p-8 rounded-2xl text-center transition-all duration-300 hover:shadow-xl hover:-translate-y-2 ${reducedMotion ? '' : 'opacity-0'
                }`}
            style={{
                background: 'var(--card-bg)',
                border: '1px solid rgba(255, 107, 53, 0.2)',
            }}
            data-card-index={index}
        >
            <div
                className="w-16 h-16 rounded-xl mx-auto mb-4 flex items-center justify-center"
                style={{ background: 'var(--gradient-fire)' }}
            >
                <Icon className="w-8 h-8 text-white" aria-hidden="true" />
            </div>

            <div className="text-4xl md:text-5xl font-display font-bold text-white mb-2">
                {displayValue}
            </div>

            <div className="text-gray-400">{label}</div>
        </div>
    );
}

const stats = [
    { icon: Clock, value: 99.9, suffix: '%', label: 'Audit Accuracy' },
    { icon: Building2, value: 5000, suffix: '+', label: 'Projects Audited' },
    { icon: Shield, value: 24, suffix: '/7', label: 'Engineer Support' },
    { icon: Heart, value: 12000, suffix: '+', label: 'Risks Mitigated' },
];

export function StatsSection() {
    const { ref, isInView } = useScrollAnimation<HTMLElement>({ threshold: 0.3 });
    const reducedMotion = useReducedMotion();
    const hasAnimated = useRef(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const [connections, setConnections] = useState<Connection[]>([]);

    // Calculate connection paths when section comes into view
    useEffect(() => {
        if (!isInView || !containerRef.current || reducedMotion) return;

        const calculatePaths = () => {
            const cards = containerRef.current?.querySelectorAll('.stat-card');
            if (!cards || cards.length < 4) return;

            const paths = calculateConnectionPaths(Array.from(cards), containerRef.current!);
            setConnections(paths);
        };

        // Calculate paths after a brief delay to ensure cards are rendered
        setTimeout(calculatePaths, 100);

        // Recalculate on resize
        const resizeHandler = createResizeHandler(calculatePaths);
        window.addEventListener('resize', resizeHandler);

        return () => window.removeEventListener('resize', resizeHandler);
    }, [isInView, reducedMotion]);

    // Animate connections after they're calculated
    useEffect(() => {
        if (connections.length === 0 || reducedMotion) return;

        // Animate each connection line drawing
        connections.forEach((_, i) => {
            setTimeout(() => {
                const pathEl = document.querySelector(`.connection-path-${i}`) as SVGPathElement;
                if (pathEl) {
                    animate(pathEl, {
                        strokeDashoffset: [pathEl.getTotalLength(), 0],
                        duration: 800,
                        ease: 'linear',
                    });
                }
            }, i * 200);
        });
    }, [connections, reducedMotion]);

    // Entrance animations
    useEffect(() => {
        if (!isInView || hasAnimated.current || reducedMotion) return;
        hasAnimated.current = true;

        animate('.stats-title', {
            translateY: [30, 0],
            opacity: [0, 1],
            duration: 800,
            ease: 'outExpo',
        });

        animate('.stat-card', {
            translateY: [50, 0],
            opacity: [0, 1],
            duration: 600,
            ease: 'outExpo',
            delay: stagger(100, { start: 200 }),
        });
    }, [isInView, reducedMotion]);

    return (
        <section
            ref={ref}
            id="stats-section"
            className="relative py-24 px-4 md:px-8 lg:px-16"
            style={{ background: 'var(--dark-bg)' }}
            aria-labelledby="stats-title"
        >
            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <h2
                        id="stats-title"
                        className={`stats-title text-4xl md:text-5xl font-display font-bold text-white mb-4 ${reducedMotion ? '' : 'opacity-0'}`}
                    >
                        Trusted by{' '}
                        <span className="text-[var(--fire-orange)]">Industry Leaders</span>
                    </h2>
                    <p className={`stats-title text-gray-400 text-lg max-w-2xl mx-auto ${reducedMotion ? '' : 'opacity-0'}`}>
                        Our track record speaks for itself. Real numbers, quantifiable risk reduction.
                    </p>
                </div>

                {/* SVG Connection Layer */}
                {!reducedMotion && connections.length > 0 && (
                    <svg
                        className="absolute inset-0 w-full h-full pointer-events-none hidden md:block"
                        style={{ zIndex: 0 }}
                        aria-hidden="true"
                    >
                        {connections.map((conn, i) => (
                            <path
                                key={i}
                                className={`connection-path-${i} connection-line`}
                                d={conn.path}
                                fill="none"
                                stroke="var(--blueprint-line)"
                                strokeWidth="2"
                                strokeDasharray={conn.length}
                                strokeDashoffset={conn.length}
                            />
                        ))}
                    </svg>
                )}

                {/* Stat Cards */}
                <div ref={containerRef} className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {stats.map((stat, index) => (
                        <StatCard
                            key={index}
                            index={index}
                            icon={stat.icon}
                            value={stat.value}
                            suffix={stat.suffix}
                            label={stat.label}
                            isInView={isInView}
                            reducedMotion={reducedMotion}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}

export default StatsSection;
